import {
  Children,
  Fragment,
  isValidElement,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import { cn } from "../cn";

// The Clevis dropdown. Drop-in for <select>: same props, <option> and
// <optgroup> children, and onChange gets an event whose target.value is the
// pick. Instead of the operating system's list it opens a branded panel with
// a check on the current choice, keyboard support, and a filter-as-you-type
// box once there are more than a handful of options. A hidden native select
// keeps `required` and `name` working inside forms.

interface Opt {
  value: string;
  label: string;
  disabled: boolean;
  group: string | null;
}

function textOf(node: ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (isValidElement<{ children?: ReactNode }>(node)) return textOf(node.props.children);
  return "";
}

function collect(children: ReactNode, group: string | null, out: Opt[]): Opt[] {
  Children.forEach(children, (child) => {
    if (!isValidElement<{ value?: unknown; children?: ReactNode; disabled?: boolean; label?: string }>(child)) return;
    if (child.type === "option") {
      const label = textOf(child.props.children).replace(/\s+/g, " ").trim();
      out.push({ value: child.props.value === undefined ? label : String(child.props.value), label, disabled: !!child.props.disabled, group });
    } else if (child.type === "optgroup") {
      collect(child.props.children, child.props.label ?? null, out);
    } else if (child.type === Fragment) {
      collect(child.props.children, group, out);
    }
  });
  return out;
}

const SEARCH_AFTER = 8;
/** A value that isn't one of the options shows as itself (an old job type), unless it's a record ID nobody can read. */
const looksLikeId = (v: string) => /^[A-Za-z0-9_-]{16,}$/.test(v) && /\d/.test(v) && /[A-Za-z]/.test(v);
const control =
  "flex items-center gap-2 rounded-lg border border-steel/40 bg-white px-3 py-2 text-left text-sm text-ink focus:border-brandorange focus:outline-none focus:ring-1 focus:ring-brandorange disabled:cursor-not-allowed disabled:bg-cream disabled:text-steel";

export interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  /** Show the filter box (default: when there are more than 8 options). */
  searchable?: boolean;
}

export function Select({
  className,
  children,
  value,
  defaultValue,
  onChange,
  disabled,
  required,
  name,
  id,
  title,
  searchable,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
}: SelectProps) {
  const options = useMemo(() => collect(children, null, []), [children]);
  const controlled = value !== undefined;
  const [inner, setInner] = useState(() => String(defaultValue ?? options[0]?.value ?? ""));
  const current = controlled ? String(value ?? "") : inner;
  const selected = options.find((o) => o.value === current);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(-1);
  const [pos, setPos] = useState<CSSProperties>({});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const typed = useRef({ text: "", at: 0 });
  const listId = useId();
  const showSearch = searchable ?? options.length > SEARCH_AFTER;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q) || (o.group ?? "").toLowerCase().includes(q));
  }, [options, query]);

  const place = useCallback(() => {
    const b = buttonRef.current?.getBoundingClientRect();
    if (!b) return;
    const below = window.innerHeight - b.bottom;
    const up = below < 240 && b.top > below;
    const maxHeight = Math.max(160, Math.min(340, (up ? b.top : below) - 12));
    const width = Math.max(b.width, 200);
    const left = Math.max(8, Math.min(b.left, window.innerWidth - width - 8));
    setPos(up ? { left, bottom: window.innerHeight - b.top + 4, width, maxHeight } : { left, top: b.bottom + 4, width, maxHeight });
  }, []);

  function openPanel() {
    if (disabled) return;
    place();
    setQuery("");
    setActive(Math.max(0, options.findIndex((o) => o.value === current)));
    setOpen(true);
  }
  function close(focusButton = true) {
    setOpen(false);
    if (focusButton) buttonRef.current?.focus();
  }
  function pick(o: Opt) {
    if (o.disabled) return;
    if (!controlled) setInner(o.value);
    if (o.value !== current) {
      const target = { value: o.value, name: name ?? "" };
      onChange?.({ target, currentTarget: target } as unknown as ChangeEvent<HTMLSelectElement>);
    }
    close();
  }

  // Keep the panel attached to its button; close on a click anywhere else.
  useLayoutEffect(() => {
    if (!open) return;
    place();
    const onScroll = (e: Event) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      place();
    };
    const onDown = (e: MouseEvent | TouchEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || buttonRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open, place]);

  useEffect(() => {
    if (!open) return;
    if (showSearch) searchRef.current?.focus();
    else listRef.current?.focus();
  }, [open, showSearch]);

  useEffect(() => {
    if (!open || active < 0) return;
    listRef.current?.querySelector<HTMLElement>(`[data-i="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [open, active]);

  const move = (from: number, step: number) => {
    if (!shown.length) return -1;
    let i = from;
    for (let n = 0; n < shown.length; n++) {
      i = (i + step + shown.length) % shown.length;
      if (!shown[i]!.disabled) return i;
    }
    return from;
  };

  function onPanelKey(e: KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => move(a, 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => move(a < 0 ? 0 : a, -1));
    } else if (e.key === "Home") {
      e.preventDefault();
      setActive(move(-1, 1));
    } else if (e.key === "End") {
      e.preventDefault();
      setActive(move(0, -1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = shown[active];
      if (o) pick(o);
    } else if (e.key === "Escape") {
      e.preventDefault();
      close();
    } else if (e.key === "Tab") {
      close(false);
    } else if (!showSearch && e.key.length === 1) {
      // Type-ahead: jump to the first option starting with what was typed.
      const t = typed.current;
      t.text = Date.now() - t.at < 700 ? t.text + e.key.toLowerCase() : e.key.toLowerCase();
      t.at = Date.now();
      const i = shown.findIndex((o) => !o.disabled && o.label.toLowerCase().startsWith(t.text));
      if (i >= 0) setActive(i);
    }
  }

  function onButtonKey(e: KeyboardEvent) {
    if (["ArrowDown", "ArrowUp", "Enter", " "].includes(e.key)) {
      e.preventDefault();
      openPanel();
    } else if (showSearch && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      openPanel();
      setQuery(e.key);
      e.preventDefault();
    }
  }

  let lastGroup: string | null = null;
  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        id={id}
        title={title}
        disabled={disabled}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cn(control, !/(^|\s)(w-|flex-1)/.test(className ?? "") && "w-full", open && "border-brandorange ring-1 ring-brandorange", className)}
        onClick={() => (open ? close() : openPanel())}
        onKeyDown={onButtonKey}
      >
        {/* Every label stacked in one cell: the control is as wide as its widest option, like a native select. */}
        <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)]">
          {options.map((o) => (
            <span key={o.value} aria-hidden={o.value !== current} className={cn("col-start-1 row-start-1 truncate", o.value !== current && "invisible")}>
              {o.label || " "}
            </span>
          ))}
          {!selected ? <span className="col-start-1 row-start-1 truncate text-steel">{current && !looksLikeId(current) ? current : "Choose…"}</span> : null}
        </span>
        <svg viewBox="0 0 20 20" className={cn("h-4 w-4 shrink-0 text-steel transition-transform", open && "rotate-180")} aria-hidden="true">
          <path d="M5.5 7.5 10 12l4.5-4.5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {/* Form semantics (required / name); visually hidden, never focused directly. */}
      <select
        tabIndex={-1}
        aria-hidden="true"
        name={name}
        required={required}
        disabled={disabled}
        value={current}
        onChange={() => undefined}
        onFocus={() => buttonRef.current?.focus()}
        style={{ position: "absolute", opacity: 0, width: 1, height: 1, pointerEvents: "none" }}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
      {open ? (
        <div
          ref={panelRef}
          className="fixed z-[1000] flex flex-col overflow-hidden rounded-xl border border-steel/25 bg-white text-sm text-ink shadow-xl shadow-ink/10"
          style={pos}
          onKeyDown={onPanelKey}
        >
          {showSearch ? (
            <div className="border-b border-steel/15 p-1.5">
              <input
                ref={searchRef}
                className="w-full rounded-md border border-steel/30 bg-cream/40 px-2.5 py-1.5 text-base text-ink placeholder:text-steel focus:border-brandorange focus:outline-none sm:text-sm"
                placeholder="Type to filter…"
                value={query}
                aria-controls={listId}
                aria-activedescendant={active >= 0 ? `${listId}-${active}` : undefined}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setActive(0);
                }}
              />
            </div>
          ) : null}
          <div ref={listRef} id={listId} role="listbox" tabIndex={-1} className="min-h-0 flex-1 overflow-y-auto py-1 outline-none">
            {shown.map((o, i) => {
              const header = o.group !== lastGroup && o.group ? o.group : null;
              lastGroup = o.group;
              const isSel = o.value === current;
              return (
                <Fragment key={`${o.group ?? ""}:${o.value}`}>
                  {header ? <div className="px-3 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-steel">{header}</div> : null}
                  <div
                    id={`${listId}-${i}`}
                    data-i={i}
                    role="option"
                    aria-selected={isSel}
                    aria-disabled={o.disabled || undefined}
                    className={cn(
                      "mx-1 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5",
                      i === active && "bg-cream",
                      isSel && "font-semibold",
                      o.disabled && "cursor-not-allowed opacity-40",
                    )}
                    onMouseEnter={() => setActive(i)}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(o)}
                  >
                    <svg viewBox="0 0 20 20" className={cn("h-4 w-4 shrink-0", isSel ? "text-brandorange" : "text-transparent")} aria-hidden="true">
                      <path d="m5 10.5 3.2 3.2L15 7" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span className="min-w-0 flex-1 truncate">{o.label || " "}</span>
                  </div>
                </Fragment>
              );
            })}
            {shown.length === 0 ? <div className="px-3 py-3 text-center text-xs text-steel">No matches</div> : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
